import {
  Title,
  Legend,
  Tooltip,
  ChartData,
  LinearScale,
  LineElement,
  ChartOptions,
  PointElement,
  CategoryScale,
  Chart as ChartJS
} from "chart.js";
import { Line } from "react-chartjs-2";
import React, { useState, useEffect } from "react";

import {
  WidgetTitle,
  PrimaryStatsInfo,
  SecondaryStatsInfo,
  WidgetHeaderContent,
  AnalyticsWidgetHeader,
  AnalyticsContentWrapper,
  AnalyticsWidgetContainer
} from "../styles";
import { colors } from "theme";
import FollowersTooltip from "./Tooltip";
import Loader from "components/common/loading/Loader";
import { getNumWithMaxDecimals } from "utils/numbers";
import { useDashboardState } from "contextApi/dashboardContext";
import { getChartData, getLineChartOptions } from "utils/charts";
import { today, difference, subtractDuration } from "utils/date";
import { externalTooltipHandler } from "../ExternalTooltipHandler";

ChartJS.register(
  Title,
  Legend,
  Tooltip,
  LinearScale,
  LineElement,
  PointElement,
  CategoryScale
);

interface IChartConfig {
  chartData: ChartData<"line">;
  chartOptions: ChartOptions<"line">;
}

const FollowersAnalytics: React.FC<{}> = () => {
  const filterRange = useDashboardState(state => state.filterRange);
  const stats = useDashboardState(state => state.stats.data);
  const isFetching = useDashboardState(state => state.stats.isFetching);
  const minDataPoints: null | number =
    typeof filterRange.periodInDays === "number"
      ? difference(
          today(),
          subtractDuration(today(), {
            days: filterRange.periodInDays
          }),
          "weeks"
        )
      : null;
  const [{ chartData, chartOptions }, setChartConfig] = useState<IChartConfig>({
    chartData: { labels: [], datasets: [] },
    chartOptions: getLineChartOptions([], {
      scales: { y: { display: false }, x: { display: false } }
    })
  });

  useEffect(() => {
    if (!isFetching && !!stats?.followers?.points) {
      const { data, dates } = getChartData(
        stats.followers.points,
        true,
        stats.followers.points.length >= 1 && !!minDataPoints
          ? minDataPoints
          : 9
      );
      const options = getLineChartOptions(data, {
        ...(stats.followers.points.length < 1
          ? {
              scales: {
                y: {
                  ticks: {
                    display: false
                  }
                }
              }
            }
          : {}),
        elements: {
          point: { radius: stats.followers.points.length === 1 ? 3 : 0 }
        },
        plugins: {
          tooltip: {
            enabled: false,
            external: externalTooltipHandler(FollowersTooltip, "left")
          }
        }
      });

      setChartConfig({
        chartData: {
          labels: dates,
          datasets: [
            {
              data
            }
          ]
        },
        chartOptions: options
      });
    }
  }, [isFetching, minDataPoints, stats]);

  const progressNumber = (() => {
    if (
      !stats?.followers ||
      typeof stats.followers.previousTotal !== "number" ||
      typeof stats.followers.total !== "number"
    ) {
      return null;
    } else {
      return stats.followers.total - stats.followers.previousTotal;
    }
  })();

  return (
    <AnalyticsWidgetContainer colspan={1} rowspan={1} bgColor={colors.green}>
      <AnalyticsWidgetHeader>
        <WidgetTitle className="alternate">Followers</WidgetTitle>
        {!isFetching && (
          <WidgetHeaderContent>
            <PrimaryStatsInfo>
              {!!stats?.followers &&
              typeof stats.followers.total === "number" &&
              !!stats?.followers?.points &&
              stats.followers.points.length >= 1
                ? stats.followers.total.toString().length >= 5
                  ? `${getNumWithMaxDecimals(stats.followers.total / 1000, 1)}k`
                  : stats.followers.total
                : "-"}
            </PrimaryStatsInfo>
            {progressNumber !== null &&
              !!stats?.followers?.points &&
              stats.followers.points.length >= 1 && (
                <SecondaryStatsInfo>
                  {progressNumber < 0 ? "-" : "+"}
                  {Math.abs(progressNumber)}
                </SecondaryStatsInfo>
              )}
          </WidgetHeaderContent>
        )}
      </AnalyticsWidgetHeader>
      <AnalyticsContentWrapper>
        {isFetching ? (
          <Loader size={36} />
        ) : (
          <Line
            width="100%"
            height="100%"
            data={chartData}
            options={chartOptions}
          />
        )}
        {!isFetching &&
          (!stats?.followers?.points || stats.followers.points.length < 1) && (
            <span className="no-data">Not enough data</span>
          )}
      </AnalyticsContentWrapper>
    </AnalyticsWidgetContainer>
  );
};

export default FollowersAnalytics;
